<script setup>
import { ref, watch, toRef, computed, useTemplateRef, toRaw, markRaw } from 'vue'
import { storeToRefs } from 'pinia'
import { useMessageStack } from '@/stores/messagestack'
import { useApi, defaultConfig } from '../../composables/api'
import PDFView from './pdf.vue'
import IMGView from './img.vue'
import { getExtension, extensions, extensionsImages } from './view'
import { useIntervalFn } from '@vueuse/core'

const emit = defineEmits(['page', 'loaded'])
const props = defineProps({
  upload: Object,
  format: {
    type: String,
    default: 'pdf',
  },
  scale: {
    type: Number,
    default: 1,
  },
  /** PDF с произвольного URL (blob: после fetch) — без docs:preview / convert */
  pdfDirectLink: {
    type: String,
    default: null,
  },
  /** Если задано — PDF: страницы 1…pdfMaxPage */
  pdfMaxPage: {
    type: Number,
    default: null,
  },
})
const { push: notify } = useMessageStack()
const { user } = storeToRefs(defaultConfig.store_user?.())

/** Сколько раз уже сбрасывали PREVIEW_FAILED после явного 422 */
const previewFailRetries = ref(0)
/** Ответы «превью ещё готовится». Дальше не крутить спиннер бесконечно. */
const previewWaitTicks = ref(0)
const PREVIEW_WAIT_LIMIT = 20
/**
 * Первый запрос и смена файла — сразу ?retry=1, чтобы не ловить 422
 * на устаревшем PREVIEW_FAILED (потом один тихий retry после нового 422).
 */
const forcePreviewRetry = ref(true)
/** Нужен пароль на открытие Excel/Word — форма поверх превью. */
const needOpenPassword = ref(false)
const openPassword = ref('')
const openPasswordError = ref('')
const openPasswordPending = ref(false)

const upLink = computed(() => {
  if (props.pdfDirectLink) {
    return null
  }
  const path = props.upload?.secure_filename
  if (!path) {
    return null
  }
  const service = props.upload?.service || defaultConfig.service || ''
  let url = `docs:preview?path=${encodeURIComponent(path)}&service=${encodeURIComponent(service)}`
  if (forcePreviewRetry.value) {
    url += '&retry=1'
  }
  return url
})

const scale = toRef(props, 'scale')
const viewEl = useTemplateRef('view-el')

const dnLink = ref(null)
const loading = ref(true)
const mime = ref(null)

const fileOk = ref(false)
const isPDF = ref(false)
const isIMG = ref(false)

watch(
  () => props.upload,
  (u, prev) => {
    if (props.pdfDirectLink) {
      return
    }
    if (!u) {
      loading.value = true
      return
    }
    const pathChanged = u.secure_filename !== prev?.secure_filename
    // Только смена файла — иначе fetchInfo после set-password
    // оставляет loading=true навсегда (preview URL тот же).
    if (pathChanged) {
      loading.value = true
    }
    const ext = getExtension(u)

    isPDF.value = ext == 'pdf'
    fileOk.value = extensions.includes(ext)
    isIMG.value = extensionsImages.includes(ext)

    if (isIMG.value) {
      mime.value = `image/${ext}`
    }

    if (!fileOk.value) {
      loading.value = false
    }
  },
  { immediate: true }
)

watch(
  () => props.pdfDirectLink,
  link => {
    if (!link) {
      return
    }
    // Прямой blob/URL: не ходим в docs:preview (иначе path=preview.pdf → 404)
    isPDF.value = true
    isIMG.value = false
    fileOk.value = true
    dnLink.value = link
    loading.value = false
  },
  { immediate: true }
)

const { status, refresh, model, error } = useApi({
  url: upLink,
  watch: [upLink],
  before: () => Boolean(props.upload?.secure_filename) && !props.pdfDirectLink,
})

const { pause, resume } = useIntervalFn(refresh)

watch(
  () => props.upload?.secure_filename,
  (path, prevPath) => {
    if (props.pdfDirectLink) {
      return
    }
    if (!path || path === prevPath) {
      return
    }
    previewFailRetries.value = 0
    previewWaitTicks.value = 0
    forcePreviewRetry.value = true
    needOpenPassword.value = false
    openPassword.value = ''
    openPasswordError.value = ''
    dnLink.value = null
    loading.value = true
    pause()
    refresh()
  }
)

/**
 * @param {string} errText
 * @returns {boolean}
 */
function isPreviewPasswordRequired(errText) {
  return (
    /preview_password_required/i.test(errText) ||
    /парол/i.test(errText) ||
    /password/i.test(errText)
  )
}

/** Таймаут POST /preview с паролем (unoserver conversion-timeout ≈ 180с). */
const OPEN_PASSWORD_TIMEOUT_MS = 120000

/**
 * Конвертация превью с паролем на открытие (POST /preview).
 */
async function submitOpenPassword() {
  const path = props.upload?.secure_filename
  const plain = String(openPassword.value || '').trim()
  if (!path) {
    return
  }
  if (!plain) {
    openPasswordError.value = 'Введите пароль'
    return
  }
  openPasswordPending.value = true
  openPasswordError.value = ''
  const ac = new AbortController()
  const timeoutId = window.setTimeout(() => ac.abort(), OPEN_PASSWORD_TIMEOUT_MS)
  try {
    const service = props.upload?.service || defaultConfig.service || ''
    const url = new URL('preview', defaultConfig.url_docparse)
    url.searchParams.set('path', path)
    url.searchParams.set('service', service)
    const headers = {
      Accept: 'application/pdf, application/json',
      'Content-Type': 'application/json',
    }
    if (user.value?.access_token) {
      headers.Authorization = `Bearer ${user.value.access_token}`
    }
    const response = await fetch(url.toString(), {
      method: 'POST',
      credentials: 'include',
      cache: 'no-cache',
      headers,
      body: JSON.stringify({ password: plain, path, service }),
      signal: ac.signal,
    })
    // 201/202 тоже ok для fetch, но это «ещё готовится», не PDF.
    if (response.status !== 200) {
      let message = 'Неправильный пароль или не удалось открыть документ'
      try {
        const data = await response.json()
        message = data?.message || data?.error_description || message
      } catch (error) {
        console.error(
          '[soykajs] не удалось разобрать ответ сервера при открытии документа по паролю',
          error
        )
        if (response.status === 201 || response.status === 202) {
          message = 'Предпросмотр ещё формируется. Повторите через несколько секунд.'
        }
      }
      openPasswordError.value = message
      return
    }
    const bytes = markRaw(new Uint8Array(await response.arrayBuffer()))
    // Ответ «preview is creating» / JSON с Content-Type text — не PDF.
    const head = String.fromCharCode(bytes[0] || 0, bytes[1] || 0, bytes[2] || 0, bytes[3] || 0)
    if (head !== '%PDF') {
      openPasswordError.value =
        'Сервер не вернул PDF. Проверьте пароль или повторите позже.'
      return
    }
    needOpenPassword.value = false
    openPassword.value = ''
    error.value = null
    pause()
    loading.value = true
    dnLink.value = bytes
  } catch (err) {
    if (err?.name === 'AbortError') {
      openPasswordError.value =
        'Превышено время ожидания конвертации. Проверьте пароль и попробуйте снова.'
    } else {
      openPasswordError.value = err?.message || 'Не удалось сформировать предпросмотр'
    }
  } finally {
    window.clearTimeout(timeoutId)
    openPasswordPending.value = false
  }
}

/**
 * Снимает спиннер превью и разблокирует тулбар модалки (карандаш и т.п.).
 * Нужен при ошибке/таймауте: иначе modal.loading ждёт PDF @loaded и зависает.
 *
 * @param {number} [pages=1] Оценка числа страниц до рендера PDF.
 */
function releasePreviewLoading(pages = 1) {
  loading.value = false
  emit('loaded', pages)
}

watch([model, status], ([model, status]) => {
  if (props.pdfDirectLink) {
    return
  }
  // Пока форма пароля / идёт POST — не трогать loading и не resume() от фонового GET.
  // Иначе смена forcePreviewRetry→upLink запускает новый GET и срывает ввод пароля.
  if (needOpenPassword.value || openPasswordPending.value) {
    return
  }
  if (status >= 400) {
    const errText = String(error.value || '')
    // Пароль на открытие: показать форму (422 required / 400 invalid).
    if (isPreviewPasswordRequired(errText)) {
      pause()
      needOpenPassword.value = true
      // retry=1 убираем после паузы и флага формы — иначе watch(upLink) шлёт ещё GET.
      forcePreviewRetry.value = false
      // Карандаш/ключ в модалке не должны ждать PDF — пароль на открытие ≠ пароль R7.
      releasePreviewLoading()
      return
    }
    // 422 = PREVIEW_FAILED: один авто-retry (сброс + пересборка), без вечного цикла
    if (status === 422 && previewFailRetries.value < 1) {
      previewFailRetries.value += 1
      forcePreviewRetry.value = true
      loading.value = true
      // Сброс превью, чтобы после retry подтянуть новый PDF
      dnLink.value = null
      refresh()
      return
    }
    forcePreviewRetry.value = false
    pause()
    const message =
      status === 422
        ? (error.value || 'Не удалось сформировать предпросмотр документа')
        : error.value
    if (message) {
      notify(message)
    }
    releasePreviewLoading()
    return
  }

  // Снимаем retry только на 201/202 (ещё нет PDF). На 200 смена URL
  // перезапрашивает bytes → destroy PDF mid-render → Transport destroyed.
  if (forcePreviewRetry.value && (status === 201 || status === 202)) {
    forcePreviewRetry.value = false
  }

  if (status === 201 || status === 202) {
    previewWaitTicks.value += 1
    if (previewWaitTicks.value >= PREVIEW_WAIT_LIMIT) {
      pause()
      notify({
        type: 'error',
        message: 'Не удалось сформировать предпросмотр документа',
      })
      releasePreviewLoading()
      return
    }
  }

  if (status == 200 && model) {
    previewWaitTicks.value = 0
    pause()
    loading.value = false
    // Не подменять уже показанный PDF новым Uint8Array того же файла.
    if (!dnLink.value) {
      const rawBytes = toRaw(model.bytes)
      dnLink.value =
        rawBytes != null ? markRaw(rawBytes) : model.bytes
    }
    // Тулбар (карандаш) не ждёт pdf.js: байты превью уже есть, страница уточнится в onLoaded.
    emit('loaded', 1)
    return
  }

  loading.value = true
  resume()
})

defineExpose({
  scale,
  jumpTo: n => {
    emit('page', n)
    viewEl.value.jumpTo(n)
  },
  print: async (...a) => viewEl.value.print(...a),
})

function onLoaded(pages, scale) {
  loading.value = false
  emit('loaded', pages, scale)
}
</script>

<template>
  <div
    class="border position-relative docview-view doc-view-view"
    :class="{ 'overflow-hidden': loading, 'overflow-y-auto': !loading }"
  >
    <div
      class="position-absolute w-100 h-100 d-flex align-items-center justify-content-center modal-backdrop"
      v-if="loading"
    >
      <span class="spinner-border"> </span>
    </div>
    <div
      v-if="needOpenPassword && !props.pdfDirectLink"
      class="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
      style="z-index: 2; background: rgba(255, 255, 255, 0.92)"
    >
      <form
        class="border rounded p-3 bg-white shadow-sm"
        style="min-width: 280px; max-width: 360px"
        @submit.prevent="submitOpenPassword"
        autocomplete="off"
      >
        <p class="mb-2">
          Документ защищён паролем на открытие. Введите пароль для предпросмотра.
        </p>
        <input
          type="password"
          class="form-control"
          :class="{ 'is-invalid': openPasswordError }"
          v-model="openPassword"
          placeholder="Пароль"
          :disabled="openPasswordPending"
          autofocus
        />
        <div v-if="openPasswordError" class="invalid-feedback d-block">
          {{ openPasswordError }}
        </div>
        <button
          class="btn btn-primary w-100 mt-3"
          type="submit"
          :disabled="openPasswordPending"
        >
          <span
            v-if="openPasswordPending"
            class="spinner-border spinner-border-sm me-1"
          />
          Открыть
        </button>
      </form>
    </div>
    <div
      class="alert alert-danger"
      v-if="!loading && error && !needOpenPassword && !dnLink && !props.pdfDirectLink"
    >
      <h4 class="alert-heading">Ошибка</h4>
      <p>Во время загрузки файла произошла ошибка</p>
      <p>{{ error }}</p>
    </div>
    <template v-if="!fileOk && !props.pdfDirectLink">
      <p class="alert alert-info">
        Просмотр данного формата файла не поддерживается. Вы можете его скачать
      </p>
    </template>
    <template v-if="dnLink && (fileOk || props.pdfDirectLink)">
      <PDFView
        :link="dnLink"
        :scale="scale"
        :max-page="props.pdfMaxPage"
        v-if="props.pdfDirectLink || (!isIMG && (isPDF || format == 'pdf'))"
        ref="view-el"
        @page="(curr, total) => $emit('page', curr, total)"
        @loaded="onLoaded"
      />
      <IMGView
        :link="dnLink"
        :mime="mime"
        :scale="scale"
        v-if="isIMG && !props.pdfDirectLink"
        ref="view-el"
        @loaded="onLoaded"
      />
    </template>
  </div>
</template>
